import { TZDate } from '@date-fns/tz';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Icon } from '@shared/icons';
import { Translate } from '@shared/translation';
import type { TimeZone } from '@shared/utils/date';
import { useDateRangeTexts } from '../useDateRangeTexts';

interface TimeZoneHintProps {
  timeZone?: TimeZone;
}

export function TimeZoneHint({ timeZone }: TimeZoneHintProps) {
  const texts = useDateRangeTexts().picker.timeZoneHint;
  const { displayOffset, isDifferent, userOffset } =
    getTimeZoneOffsets(timeZone);

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Icon
        name={isDifferent ? 'warning' : 'info'}
        color={isDifferent ? 'warning' : 'text.secondary'}
        size="small"
      />
      <Box>
        <Typography variant="body2">
          <Translate
            text={texts.viewingInTimeZone}
            values={{ offset: displayOffset }}
          />
        </Typography>
        {isDifferent && (
          <Typography variant="body2">
            <Translate text={texts.youAreIn} values={{ offset: userOffset }} />
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function getTimeZoneOffsets(timeZone?: TimeZone) {
  const displayOffset = timeZone
    ? getUtcOffsetWithSign(new TZDate(new Date(), timeZone))
    : getUtcOffsetWithSign();
  const userOffset = getUtcOffsetWithSign();

  return {
    displayOffset,
    isDifferent: displayOffset !== userOffset,
    userOffset,
  };
}

function getUtcOffset(date: Date = new Date()) {
  return -(date.getTimezoneOffset() / 60);
}

function getUtcOffsetWithSign(date: Date = new Date()) {
  const offset = getUtcOffset(date);

  return offset >= 0 ? `+${offset}` : `${offset}`;
}
